import { makeStyles } from '@material-ui/core/styles'
import {
  PGA_COACH_ERROR_BG,
  PGA_COACH_ERROR_TEXT,
} from 'lib/styles/pga-coach-pill-colors'
// Tokens for the redesigned group event registration flow. Colors come from
// the `offeringsRefresh` theme (navy / gold / beige); only neutrals that the
// theme does not define live here.
export const PAGE_BACKGROUND = '#F5F6F8'
export const SOFT_PANEL_BACKGROUND = '#F5F6F8'
export const CARD_BORDER = '#E6E8EC'
export const CARD_SHADOW =
  '0 1px 2px rgba(0, 10, 22, 0.04), 0 8px 24px rgba(0, 10, 22, 0.04)'
export const MUTED_TEXT = '#5F6B76'
export const SUMMARY_WIDTH = 340

const useStyles = makeStyles(theme => ({
  page: {
    backgroundColor: PAGE_BACKGROUND,
    minHeight: '100%',
    // The nested theme's body typography still resolves to the base theme's
    // Inter, so set the refresh font here and let plain text and form
    // controls inherit it.
    fontFamily: theme.typography.fontFamily,
    // Class names are suffixed inside the nested theme, so match by prefix.
    '& [class*="MuiInputBase-root"], & input, & textarea, & label, & legend': {
      fontFamily: 'inherit',
    },
    '& input, & textarea': {
      fontWeight: 400,
    },
  },
  inner: {
    maxWidth: 1080,
    margin: '0 auto',
    padding: theme.spacing(4, 4, 8),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 6),
    },
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: 0,
    marginBottom: theme.spacing(3),
    color: MUTED_TEXT,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },
    '& svg': { fontSize: 18 },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2, 3),
    marginBottom: theme.spacing(4),
  },
  eyebrow: {
    display: 'block',
    marginBottom: theme.spacing(1),
    color: MUTED_TEXT,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 40,
    lineHeight: 1.1,
    [theme.breakpoints.down('xs')]: {
      fontSize: 30,
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) ${SUMMARY_WIDTH}px`,
    gap: theme.spacing(4),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(3),
    },
  },
  mainCol: {
    minWidth: 0,
  },
  sideCol: {
    minWidth: 0,
    position: 'sticky',
    top: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      position: 'static',
    },
  },
  sideColFirstOnMobile: {
    [theme.breakpoints.down('sm')]: {
      order: -1,
    },
  },

  // Step pills
  pills: {
    display: 'flex',
    alignItems: 'center',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    height: 32,
    padding: theme.spacing(0, 1.75),
    borderRadius: 16,
    border: `1px solid ${CARD_BORDER}`,
    backgroundColor: theme.palette.common.white,
    color: MUTED_TEXT,
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    '& svg': { fontSize: 16 },
  },
  pillActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  pillDone: {
    borderColor: theme.palette.secondary.light,
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
  },
  pillConnector: {
    width: 24,
    height: 1,
    backgroundColor: CARD_BORDER,
    [theme.breakpoints.down('xs')]: {
      width: 8,
    },
  },

  // Cards
  card: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 24,
    border: `1px solid ${CARD_BORDER}`,
    boxShadow: CARD_SHADOW,
    padding: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.5),
      borderRadius: 20,
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontSize: 22,
    lineHeight: 1.25,
  },
  sectionHint: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2),
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2, 3),
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${CARD_BORDER}`,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
    },
  },
  footerEnd: {
    justifyContent: 'flex-end',
  },
  legal: {
    maxWidth: 280,
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.5,
    '& a': {
      color: 'inherit',
      textDecoration: 'underline',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: 'none',
    },
  },
  // Color, radius, height and padding come from the theme's contained
  // primary button so this matches every other CTA.
  primaryButton: {},
  textButton: {
    height: 48,
    borderRadius: 24,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.06)',
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },
  },

  // Participants
  counterList: {
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
    '& > * + *': {
      borderTop: `1px solid ${CARD_BORDER}`,
    },
  },
  counterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
  },
  counterLabel: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  counterCaption: {
    color: MUTED_TEXT,
    fontSize: 13,
  },
  counterControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  counterButton: {
    boxSizing: 'border-box',
    width: 36,
    height: 36,
    padding: 0,
    border: `1px solid ${CARD_BORDER}`,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: SOFT_PANEL_BACKGROUND,
    },
    '&:focus': { outline: 'none' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.lightest}`,
      outlineOffset: 2,
    },
    '& svg': { fontSize: 18 },
  },
  counterValue: {
    minWidth: 20,
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 700,
  },
  participantBlock: {
    marginTop: theme.spacing(3),
  },
  inlineError: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    backgroundColor: PGA_COACH_ERROR_BG,
    color: PGA_COACH_ERROR_TEXT,
    fontSize: 14,
    lineHeight: 1.4,
    '& svg': { flexShrink: 0, fontSize: 18 },
  },
  participantTitle: {
    marginBottom: theme.spacing(1.5),
    fontSize: 16,
    fontWeight: 700,
  },

  // Form fields: labels sit above the control. This sheet renders inside the
  // nested `offeringsRefresh` ThemeProvider, where MUI suffixes its class names
  // (`MuiInputLabel-root-123`), so style by element instead of `.Mui*` classes.
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2.5, 2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
    // `data-shrink` is set on every MUI InputLabel; matching it outranks the
    // shrunken-label rule (translate + scale 0.75) that applies once filled.
    '& label, & label[data-shrink]': {
      position: 'static',
      display: 'block',
      transform: 'none',
      marginBottom: theme.spacing(0.75),
      color: theme.palette.text.primary,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.4,
      pointerEvents: 'auto',
    },
    '& label.Mui-focused, & label[data-shrink].Mui-focused': {
      color: theme.palette.text.primary,
    },
    '& label.Mui-error, & label[data-shrink].Mui-error': {
      color: theme.palette.error.main,
    },
    '& fieldset': {
      top: 0,
    },
    '& legend': {
      display: 'none',
    },
  },
  fieldFull: {
    gridColumn: '1 / -1',
  },
  hidden: {
    display: 'none',
  },
  signedInCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
  },
  nudge: {
    marginBottom: theme.spacing(2),
    '&:empty': { display: 'none' },
  },
  // Re-tints the shared TipWell with the redesign palette (soft panel, card
  // border, gold icon like the summary card). `&&` outranks TipWell's own sheet
  // regardless of stylesheet injection order.
  nudgeCallout: {
    '&&': {
      backgroundColor: SOFT_PANEL_BACKGROUND,
      border: `1px solid ${CARD_BORDER}`,
    },
  },
  nudgeIcon: {
    '&&': { color: theme.palette.secondary.main },
  },
  sameAsFirst: {
    marginTop: theme.spacing(-0.5),
    marginBottom: theme.spacing(1.5),
  },

  // Marketing opt-in
  optIn: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderRadius: 16,
    backgroundColor: SOFT_PANEL_BACKGROUND,
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1.5),
      padding: theme.spacing(2.5),
    },
  },
  optInCheckbox: {
    padding: theme.spacing(0.5),
    color: theme.palette.primary.main,
    '&.Mui-checked': { color: theme.palette.primary.main },
    // 44px touch target; negative margins keep the 24px icon on the card's
    // padding edge so the target adds no extra height or width.
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.25),
      margin: theme.spacing(-1.25),
    },
  },
  optInHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
  },
  optInLogo: {
    flexShrink: 0,
  },
  optInTitle: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.3,
  },
  optInBody: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },
  optInConsent: {
    margin: theme.spacing(1, 0, 0),
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.5,
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1.5),
    },
  },

  // Review & pay
  detailsCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5, 3),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
  },
  detailsHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  detailsTitle: {
    fontSize: 20,
  },
  editLink: {
    padding: 0,
    minWidth: 0,
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 700,
    textDecoration: 'underline',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2, 3),
    margin: 0,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  detailLabel: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 13,
  },
  detailValue: {
    margin: 0,
    overflowWrap: 'anywhere',
    fontSize: 15,
    fontWeight: 600,
  },
  securePanel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
    borderRadius: 16,
    backgroundColor: SOFT_PANEL_BACKGROUND,
    '& svg': {
      flexShrink: 0,
      marginTop: 2,
      color: theme.palette.secondary.main,
      fontSize: 20,
    },
  },
  securePanelTitle: {
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  securePanelBody: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },

  // Waiver
  waiverBody: {
    maxHeight: 420,
    overflowY: 'auto',
    padding: theme.spacing(2, 2.5),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
  },
  waiverAgree: {
    marginTop: theme.spacing(3),
  },

  // Event summary
  summary: {
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
    borderRadius: 24,
    border: `1px solid ${CARD_BORDER}`,
    boxShadow: CARD_SHADOW,
  },
  summaryImage: {
    position: 'relative',
    height: 168,
    backgroundColor: theme.palette.primary.main,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    [theme.breakpoints.down('xs')]: {
      height: 72,
    },
  },
  spotsPill: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 14,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: 700,
  },
  summaryBody: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2, 0.5),
    },
  },
  summaryEyebrow: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    color: MUTED_TEXT,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
  },
  summaryTitle: {
    marginBottom: theme.spacing(2),
    fontSize: 24,
    lineHeight: 1.2,
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(1),
      fontSize: 20,
    },
  },
  // Date and time share a line on phones when they fit, and wrap otherwise.
  factPair: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: theme.spacing(2),
    },
  },
  factRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.25),
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.45,
    '& svg': {
      flexShrink: 0,
      marginTop: 1,
      color: theme.palette.secondary.main,
      fontSize: 18,
    },
    '& a': {
      color: 'inherit',
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.35,
    },
  },
  factSecondary: {
    display: 'block',
    fontSize: 13,
    opacity: 0.85,
  },
  summaryDivider: {
    height: 1,
    margin: theme.spacing(2.5, 0),
    border: 0,
    backgroundColor: CARD_BORDER,
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(1.25, 0),
    },
  },
  coachRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  coachAvatar: {
    width: 48,
    height: 48,
    border: `2px solid ${theme.palette.secondary.light}`,
    backgroundColor: theme.palette.grey.light,
    color: theme.palette.common.white,
    fontSize: 18,
    [theme.breakpoints.down('xs')]: {
      width: 40,
      height: 40,
      fontSize: 16,
    },
  },
  coachName: {
    fontSize: 17,
    lineHeight: 1.25,
    [theme.breakpoints.down('xs')]: {
      fontSize: 16,
    },
  },
  coachTitle: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5, 1),
    marginTop: theme.spacing(0.5),
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.4,
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(0.25),
    },
  },
  memberChip: {
    height: 24,
    backgroundColor: '#E9ECF3',
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: 700,
  },
  messageButton: {
    marginLeft: 'auto',
    boxSizing: 'border-box',
    flexShrink: 0,
    width: 40,
    height: 40,
    padding: 0,
    border: `1px solid ${CARD_BORDER}`,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    '&:hover': { backgroundColor: SOFT_PANEL_BACKGROUND },
    '& svg': { fontSize: 18 },
    // Keep a 44px tap target on phones.
    [theme.breakpoints.down('xs')]: {
      width: 44,
      height: 44,
    },
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    color: MUTED_TEXT,
    fontSize: 14,
  },
  dueRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(0.5),
    },
  },
  dueLabel: {
    fontSize: 16,
    fontWeight: 700,
  },
  dueAmount: {
    fontSize: 28,
    lineHeight: 1.1,
    [theme.breakpoints.down('xs')]: {
      fontSize: 22,
    },
  },
  priceNote: {
    margin: theme.spacing(1, 0, 0),
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.5,
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(0.5),
      lineHeight: 1.4,
    },
  },
  aboutToggle: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 0,
    border: 0,
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    textAlign: 'left',
    '&:focus': { outline: 'none' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.lightest}`,
      outlineOffset: 2,
    },
    '& svg': { fontSize: 20 },
    // Keep a 44px tap target on phones.
    [theme.breakpoints.down('xs')]: {
      minHeight: 44,
    },
  },
  aboutBody: {
    maxHeight: 320,
    overflowY: 'auto',
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.primary,
    fontSize: 14,
    lineHeight: 1.6,
    '& p': { margin: theme.spacing(0, 0, 1.5) },
  },
}))

export default useStyles
